// @flow
import LoadingModal from 'components/LoadingModal'
import isNil from 'lodash/isNil'
import max from 'lodash/max'
import split from 'lodash/split'
import startsWith from 'lodash/startsWith'
import { FormBuilderContext } from 'pages/Contexts/FormBuilderContext'
import * as React from 'react'
import { DragDropContext } from 'react-beautiful-dnd'
import { withRouter } from 'react-router-dom'
import ErrorLabel from 'ui/atoms/ErrorLabel'
import Text from 'ui/atoms/Text'
import {
  FORM_NAME_ERROR_MESSAGE,
  SELECTION_TYPE,
} from 'utils/FormBuilder/ConstantsFormBuilder'
import { hasText } from 'utils/helpers'

import cn from '@/utils/tailwind/cn'
import { ZentakeContext } from '@/utils/ZentakeContext'

import BuilderAddButton from './Components/BuilderAddButton'
import BuilderPageTree from './Components/BuilderPageTree'
import BuilderSelectableItem from './Components/BuilderSelectableItem'

function FormBuilder() {
  const {
    addPage,
    addRowInBetween,
    deleting,
    formErrors,
    formName,
    getQuestion,
    onReorderQuestion,
    onReorderQuestionToNewRow,
    pagesTreeArray,
    questionIdToScroll,
    setIsReordering,
    setQuestionIdToScroll,
    setReorderColumns,
    setReorderSourceRow,
    setSelectedItem,
    measuresEnabledForm,
  } = React.useContext(FormBuilderContext)
  const { teamDetails } = React.useContext(ZentakeContext)
  const measuresEnabled = teamDetails.measures

  const formTitleRef: { current: any } = React.useRef()

  const nameToDisplay = React.useMemo(
    // () => formTextName || formName,
    () => formName,
    [formName]
  )

  React.useEffect(() => {
    if (questionIdToScroll === 'FORM_TITLE') {
      window.scrollTo({
        left: 0,
        top: max([(formTitleRef?.current?.offsetTop || 0) - 32, 0]),
        behavior: 'smooth',
      })
      setQuestionIdToScroll(null)
    }
  }, [questionIdToScroll, setQuestionIdToScroll])

  const hasFormNameError = formErrors.includes(FORM_NAME_ERROR_MESSAGE)
  const filteredFormErrors = [...formErrors]

  if (hasFormNameError) {
    filteredFormErrors.splice(formErrors.indexOf(FORM_NAME_ERROR_MESSAGE), 1)
  }

  const onStartReorder = React.useCallback(
    (result) => {
      setIsReordering(true)
      setReorderSourceRow(result?.source?.droppableId)
      const draggedQuestion = getQuestion(result?.draggableId)
      setReorderColumns(draggedQuestion?.display_columns ?? 12)
    },
    [setIsReordering, getQuestion, setReorderColumns, setReorderSourceRow]
  )

  const onReorder = React.useCallback(
    (result) => {
      if (result.type === 'TASK') {
        setIsReordering(false)
        setReorderColumns(0)
        setReorderSourceRow(null)
        if (
          isNil(result?.draggableId) ||
          isNil(result?.source?.droppableId) ||
          isNil(result?.destination?.droppableId) ||
          isNil(result?.source?.index) ||
          isNil(result?.destination?.index) ||
          (result.source.droppableId === result.destination.droppableId &&
            result.source.index === result.destination.index)
        ) {
          return
        }

        const { droppableId } = result.destination

        if (startsWith(droppableId, 'addRowTo_')) {
          const [, sectionId] = split(droppableId, '_')
          onReorderQuestionToNewRow({
            questionId: result.draggableId,
            toSectionId: sectionId,
          })
          setSelectedItem({
            id: result.draggableId,
            type: SELECTION_TYPE.QUESTION,
          })
          return
        }

        if (startsWith(droppableId, 'blueline')) {
          const [, sectionId, rowId] = droppableId.split('_')
          const questionId = result.draggableId
          addRowInBetween(sectionId, rowId, false, questionId, true)
          setSelectedItem({
            id: questionId,
            type: SELECTION_TYPE.QUESTION,
          })
          return
        }

        if (startsWith(droppableId, 'firstblueline')) {
          const [, sectionId, rowId] = droppableId.split('_')
          const questionId = result.draggableId
          addRowInBetween(sectionId, rowId, true, questionId, true)
          setSelectedItem({
            id: questionId,
            type: SELECTION_TYPE.QUESTION,
          })
          return
        }

        onReorderQuestion({
          questionId: result.draggableId,
          fromRowId: result.source.droppableId,
          toRowId: result.destination?.droppableId,
          toRowIndex: result.destination?.index,
        })
        setSelectedItem({
          id: result.draggableId,
          type: SELECTION_TYPE.QUESTION,
        })
      }
    },
    [
      setReorderSourceRow,
      setIsReordering,
      setReorderColumns,
      onReorderQuestion,
      onReorderQuestionToNewRow,
      addRowInBetween,
      setSelectedItem,
    ]
  )

  if (deleting) return <LoadingModal />

  return (
    <DragDropContext onDragEnd={onReorder} onDragStart={onStartReorder}>
      <div
        className={cn({
          'pointer-events-none cursor-not-allowed select-none opacity-60':
            measuresEnabledForm && measuresEnabled,
        })}
      >
        <BuilderSelectableItem
          titleVariant
          className="mb-4"
          hasErrors={hasFormNameError}
          id={SELECTION_TYPE.FORM}
          type={SELECTION_TYPE.FORM}
        >
          <>
            <Text
              $medium
              $xlarge
              $muted={!hasText(nameToDisplay)}
              className="py-2"
              ref={formTitleRef}
            >
              {hasText(nameToDisplay) ? nameToDisplay : 'Form name'}
            </Text>
            {hasFormNameError && (
              <p>
                <ErrorLabel>{FORM_NAME_ERROR_MESSAGE}</ErrorLabel>
              </p>
            )}
          </>
        </BuilderSelectableItem>
        <div>
          {pagesTreeArray.map((page, i) => (
            <BuilderPageTree index={i} key={page?.id ?? i} page={page} />
          ))}
        </div>
        <BuilderAddButton onClick={() => addPage(false)} spacingTop={4}>
          + new section
        </BuilderAddButton>
        <div className="mb-5">
          {filteredFormErrors &&
            filteredFormErrors.map((error, index) => (
              <p key={index}>
                <ErrorLabel>{error}</ErrorLabel>
              </p>
            ))}
        </div>
      </div>
    </DragDropContext>
  )
}

const EnhancedFormBuilder: React.ComponentType<{}> = withRouter(FormBuilder)

export default EnhancedFormBuilder
